/**
 * UsageLayers — a month's feedback in layers (SF-5095): surveys first with each survey type
 * inside it, then old style ratings. Shared by the dashboard and the company Health tab, which
 * passes `compare` to set a second month beside the first (SF-5096). With `compare` the table
 * also opens on total feedback and closes each row with the % change (SF-5117), and `perDay`
 * adds a per-day column beside each month for the rows the API averages. SF-5124: labels and
 * headings stay on one line; the table scrolls sideways only if its card is still too narrow.
 */
import { Typography, theme } from 'antd';
import { EM_DASH, formatNumber } from '../../utils/format';
import { TrendText } from './TrendText';
import { SURVEY_TYPE_KEYS, type DailyAverage, type UsageTotals } from './usageClient';
import { USAGE_TYPE_LABELS, surveysTotal } from './usageNumbers';
import { dailyValue, formatPerDay } from './usageProgress';

const { Text } = Typography;

export interface UsageLayersProps {
  totals: UsageTotals;
  /** Accessible name of the breakdown table. */
  label?: string;
  /** A second month shown in its own column, with a heading over each column. */
  compare?: {
    previous: UsageTotals;
    currentLabel: string;
    previousLabel: string;
    /** Heading of the change column (e.g. "Change vs same days last month"). */
    changeLabel?: string;
    /** Per-day figures for each month; leave out to show no per-day columns. */
    perDay?: { current: DailyAverage | null; previous: DailyAverage | null };
  };
}

interface LayerRow {
  key: string;
  label: string;
  current: number;
  previous: number;
  /** The daily average field for this row; survey types have none. */
  perDay?: keyof DailyAverage;
  child?: boolean;
}

function layerRows(totals: UsageTotals, previous: UsageTotals | undefined): LayerRow[] {
  const prev = previous ?? totals;
  const rows: LayerRow[] = [];
  if (previous) {
    const total = (t: UsageTotals) => surveysTotal(t) + (t.ratings ?? 0);
    rows.push({
      key: 'total',
      label: 'Total feedback',
      current: total(totals),
      previous: total(prev),
      perDay: 'total_feedback',
    });
  }
  rows.push({
    key: 'surveys',
    label: 'Surveys',
    current: surveysTotal(totals),
    previous: surveysTotal(prev),
    perDay: 'surveys',
  });
  for (const key of SURVEY_TYPE_KEYS) {
    rows.push({
      key,
      label: USAGE_TYPE_LABELS[key],
      current: totals.surveys?.by_type?.[key] ?? 0,
      previous: prev.surveys?.by_type?.[key] ?? 0,
      child: true,
    });
  }
  rows.push({
    key: 'ratings',
    label: USAGE_TYPE_LABELS.ratings,
    current: totals.ratings ?? 0,
    previous: prev.ratings ?? 0,
    perDay: 'ratings',
  });
  return rows;
}

export const UsageLayers: React.FC<UsageLayersProps> = ({
  totals,
  label = 'Feedback breakdown',
  compare,
}) => {
  const { token } = theme.useToken();
  const cell: React.CSSProperties = {
    padding: '4px 8px',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
  };
  const figure: React.CSSProperties = { ...cell, textAlign: 'right' };
  const perDay = compare?.perDay;
  const perDayCell = (avg: DailyAverage | null, row: LayerRow) => {
    if (!row.perDay) return <td style={figure} />;
    const v = dailyValue(avg, row.perDay);
    return (
      <td style={figure}>
        <Text type="secondary">{v === null ? EM_DASH : formatPerDay(v)}</Text>
      </td>
    );
  };
  const heading = (text: string) => (
    <th scope="col" style={figure}>
      {text}
    </th>
  );

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <table aria-label={label} style={{ width: '100%', borderCollapse: 'collapse' }}>
        {compare && (
          <thead>
            <tr>
              <td style={cell} />
              {heading(compare.currentLabel)}
              {perDay && heading('Per day')}
              {heading(compare.previousLabel)}
              {perDay && heading('Per day')}
              {heading(compare.changeLabel ?? 'Change')}
            </tr>
          </thead>
        )}
        <tbody>
          {layerRows(totals, compare?.previous).map((row) => {
            const weight = row.child ? undefined : 600;
            return (
              <tr key={row.key}>
                <th
                  scope="row"
                  style={{
                    ...cell,
                    textAlign: 'left',
                    ...(row.child ? { paddingLeft: 32, fontWeight: 400 } : {}),
                  }}
                >
                  {row.child ? <Text type="secondary">{row.label}</Text> : row.label}
                </th>
                <td style={{ ...figure, fontWeight: weight }}>{formatNumber(row.current)}</td>
                {perDay && perDayCell(perDay.current, row)}
                {compare && (
                  <td style={{ ...figure, fontWeight: weight }}>{formatNumber(row.previous)}</td>
                )}
                {perDay && perDayCell(perDay.previous, row)}
                {compare && (
                  <td style={figure}>
                    <TrendText current={row.current} previous={row.previous} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
